import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { tablesConfig, tables } from '../../constants';
import {
  usePagination,
  useFetchPaginatedData,
  useFetchDataOnFilterChange,
  usePolling,
} from '../../hooks';
import TableSkeleton from './TableSkeleton';
import PaginatorV2 from './PaginatorV2';
import { normalizeEventsForUsageHistory } from '../../utils/normalizeEvents';
import _ from 'lodash';

export default function UsageHistoryTableView({
  children,
  filterParams = {},
  areFiltersActive = false,
  recordsPerPage = 10,
  pollingMs = 15000,
}) {
  const [uiPage, setUiPage] = useState(1);

  // Flags de UI
  const [initialLoad, setInitialLoad] = useState(true); // solo primer render
  const [pageLoading, setPageLoading] = useState(false); // carga por cambio de página

  // Config de Usage
  const dataType = tables.usage;
  const config = tablesConfig[dataType];
  if (!config) {
    console.error('UsageHistoryTableView: dataType "usage" no está en tablesConfig');
  }

  // Filtros limpios y memoizados
  const filterParamsMemo = useMemo(() => {
    const base = {};
    if (filterParams?.event_id) base.event_id = filterParams.event_id;
    if (filterParams?.metric_id) base.metric_id = filterParams.metric_id;
    if (filterParams?.start_date) base.start_date = filterParams.start_date;
    if (filterParams?.end_date) base.end_date = filterParams.end_date;
    if (typeof filterParams?.show_transit === 'boolean')
      base.show_transit = filterParams.show_transit;
    return base;
  }, [filterParams]);

  // Fetch paginado (server-side) para usage
  const { data, error, fetchData, totalItems, fetchDataWithFilters } = useFetchPaginatedData(
    config?.fetchFunction,
    filterParamsMemo,
    recordsPerPage,
    uiPage,
    config?.fetchFunctionWithFilters,
    dataType,
  );

  // ---- DEDUP + GUARDS ----
  // Evita fetches duplicados + pausa polling mientras hay request
  const inFlightRef = useRef(false);
  const lastPageRef = useRef(null); // dedupe por página

  const doFetchClean = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await fetchData(true); // limpia data -> skeleton solo si initialLoad || pageLoading
    } finally {
      inFlightRef.current = false;
      // primer carga ya terminó
      if (initialLoad) setInitialLoad(false);
      // si veníamos de cambio de página, lo cerramos
      if (pageLoading) setPageLoading(false);
    }
  }, [fetchData, initialLoad, pageLoading]);

  // Refetch cuando cambian filtros (SIN limpiar; mantiene la tabla y evita skeleton)
  useFetchDataOnFilterChange(filterParamsMemo, fetchData, fetchDataWithFilters);

  // Polling (solo si no hay request en vuelo ni pageLoading)
  const pollOnce = useCallback(() => {
    if (inFlightRef.current || pageLoading) return;
    const run = areFiltersActive ? fetchDataWithFilters : fetchData;
    run(); // sin limpiar la tabla
  }, [areFiltersActive, fetchData, fetchDataWithFilters, pageLoading]);

  usePolling(pollingMs ? pollOnce : () => {}, pollingMs || 0);

  // Normalización de datos
  const rawData = useMemo(() => (_.isArray(data) ? data : []), [data]);
  const normalizedData = useMemo(() => normalizeEventsForUsageHistory(rawData), [rawData]);

  // Paginación client-side (usa normalizedData pero se sincroniza con el server)
  const { currentPage, currentRecords, totalPages, getPaginatedPageNumbers, handlePageChange } =
    usePagination(normalizedData, recordsPerPage, totalItems, null);

  // Sincronía UI ↔ hook de paginación
  useEffect(() => {
    setUiPage(currentPage);
  }, [currentPage]);

  // Fetch SOLO al cambiar de página (limpio). Filtros los maneja el hook anterior sin limpiar.
  useEffect(() => {
    if (lastPageRef.current === uiPage) return;
    lastPageRef.current = uiPage;
    doFetchClean();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiPage, doFetchClean]);

  // Proxy para el paginador
  const onPageChange = useCallback(
    (page) => {
      const next = Math.max(1, Math.min(page, totalPages || page));
      // marcamos que viene un fetch por cambio de página
      setPageLoading(true);
      handlePageChange(next); // actualiza paginación local
      setUiPage(next); // dispara el useEffect -> doFetchClean()
    },
    [handlePageChange, totalPages],
  );

  // Estados de UI
  const tableIsEmpty = currentRecords.length === 0;
  const showSkeleton = tableIsEmpty && (initialLoad || pageLoading);
  const showTable = !showSkeleton && !error;

  return (
    <div className="flex flex-col flex-grow">
      {/* Loading */}
      {showSkeleton && (
        <div className="flex-grow overflow-auto">
          <TableSkeleton columns={config?.columns || []} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="relative p-8 text-red-500 flex justify-center items-center w-full">
          {error}
        </div>
      )}

      {/* Tabla + paginador */}
      {showTable && (
        <>
          <div className="flex-grow overflow-y-auto">
            <div className="overflow-x-auto">
              {children({
                currentRecords,
                columns: config?.columns || [],
                rawData,
              })}
            </div>
          </div>

          <div className="mt-2">
            <PaginatorV2
              getPaginatedPageNumbers={getPaginatedPageNumbers}
              currentPage={currentPage}
              onPageChange={onPageChange}
              total={totalPages}
              goToPrevPage={() => onPageChange(currentPage - 1)}
              goToNextPage={() => onPageChange(currentPage + 1)}
            />
          </div>
        </>
      )}
    </div>
  );
}
